// Chart JS
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js'
import { DashboardFilter, HeaderMenu } from "components"
import { SummaryCard } from "./components"

import moment from "moment"
import { useTotalPurchaseSpend, useChartList, useTotalSupplierPurchase } from "./hook"
import { useEffect, useState } from "react"
import { SummaryType } from "./types"
import { convertNumber } from "utils"


// Utils
import { cardList } from "./utils"

//Types
import { DashboardFillType } from "types"
import { SummaryBar } from './components/SummaryBar'

ChartJS.register(ArcElement, Tooltip)

export default function Dashboard() {
	const yearMonthFormat = (dateString: string) => {
	  const [year, month] = dateString.split("-");
	  return `${year}${month}`;
	};
	const paramsMonth = {
	  year_month: yearMonthFormat(moment().startOf("month").format("YYYY-MM-DD")), 
	  location_id: ""
	}

	const totalPurchaseSpend = useTotalPurchaseSpend(paramsMonth) 
	const totalSupplierPurchase = useTotalSupplierPurchase(paramsMonth)

	const [topCardList, setTopCardList] = useState<SummaryType[]>(cardList.top);
	const [bottomCardList, setBottomCardList] = useState<SummaryType[]>(cardList.bottom);

	useEffect(() => {
		if (totalPurchaseSpend.data) {
		  const apiData = totalPurchaseSpend.data;
		  setTopCardList([
			{
			  label: `COUNT: ${apiData.jumlah_po_spend ? convertNumber(Number(apiData.jumlah_po_spend)).withoutDecimal : "0"} PO`,
			  sideLabel: "TOTAL PURCHASE SPEND",
			  value: apiData.total_po_spend ? convertNumber(Number(apiData.total_po_spend)).intoCurrency : "0",
			},
			{
			  label: `COUNT: ${apiData.jumlah_po_pending ? convertNumber(Number(apiData.jumlah_po_pending)).withoutDecimal : "0"} PO`,
			  sideLabel: "TOTAL PENDING ORDER",
			  value: apiData.total_po_pending ? convertNumber(Number(apiData.total_po_pending)).intoCurrency : "0",
			},
			{
			  sideLabel: "AVERAGE PURCHASE",
			  value: apiData.avg_purchase ? convertNumber(Number(apiData.avg_purchase)).intoCurrency : "0",
			},
			{
			  sideLabel: "PURCHASE ORDER CYCLE TIME",
			  value: `${apiData.purchase_order_cycle ? convertNumber(Number(apiData.purchase_order_cycle)).intoNormalAmount : "0"} DAYS`,
			},
			{
			  sideLabel: "ONTIME DELIVERY",
			  value: `${apiData.ontime_delivery ? convertNumber(Number(apiData.ontime_delivery)).intoNormalAmount : "0"} DAYS`,
			},
			{
			  sideLabel: "PURCHASE TO SALE RATIO",
			  value: `${apiData.purchase_sale_ratio ? convertNumber(Number(apiData.purchase_sale_ratio)).intoNormalAmount : "0"}%`,
			},
			{
			  sideLabel: "STOCK TURN OVER RATE",
			  value: `${apiData.stock_turn_over ? convertNumber(Number(apiData.stock_turn_over)).intoNormalAmount : "0"} DAYS`,
			},
		  ]);
		}
	}, [totalPurchaseSpend.data]);


	useEffect(() => {
		if (totalSupplierPurchase.data) {
		  const apiData = totalSupplierPurchase.data;
		  setBottomCardList([
			{
			  label: `COUNT: ${apiData.jumlah_pay_supplier ? convertNumber(Number(apiData.jumlah_pay_supplier)).withoutDecimal : "0"}`,
			  sideLabel: "TOTAL PAYMENT TO SUPPLIER",
			  value: apiData.total_pay_supplier ? convertNumber(Number(apiData.total_pay_supplier)).intoCurrency : "0",
			},
			{
			  label: `COUNT: ${apiData.jumlah_deposit_supplier ? convertNumber(Number(apiData.jumlah_deposit_supplier)).withoutDecimal : "0"}`,
			  sideLabel: "TOTAL DEPOSIT TO SUPPLIER",
			  value: apiData.total_deposit_supplier ? convertNumber(Number(apiData.total_deposit_supplier)).intoCurrency : "0",
			},
			{
			  label: `COUNT: ${apiData.jumlah_credit_supplier ? convertNumber(Number(apiData.jumlah_credit_supplier)).withoutDecimal : "0"}`,
			  sideLabel: "TOTAL CREDIT TO SUPPLIER",
			  value: apiData.total_credit_supplier ? convertNumber(Number(apiData.total_credit_supplier)).intoCurrency : "0",
			},
			{
			  label: `COUNT: ${apiData.jumlah_payable_supplier ? convertNumber(Number(apiData.jumlah_payable_supplier)).withoutDecimal : "0"}`,
			  sideLabel: "TOTAL PAYABLE TO SUPPLIER",
			  value: apiData.total_payable_supplier ? convertNumber(Number(apiData.total_payable_supplier)).intoCurrency : "0",
			},
			{
			  label: `COUNT: ${apiData.jumlah_unit_on_order ? convertNumber(Number(apiData.jumlah_unit_on_order)).withoutDecimal : "0"}`,
			  sideLabel: "TOTAL UNIT ON ORDER",
			  value: apiData.total_unit_on_order ? convertNumber(Number(apiData.total_unit_on_order)).intoCurrency : "0",
			},
			{
			  label: `COUNT: ${apiData.jumlah_unit_delivered ? convertNumber(Number(apiData.jumlah_unit_delivered)).withoutDecimal : "0"}`,
			  sideLabel: "TOTAL UNIT DELIVERED",
			  value: apiData.total_unit_delivered ? convertNumber(Number(apiData.total_unit_delivered)).intoCurrency : "0",
			},
			{
			  label: `COUNT: ${apiData.total_unit_rejected ? convertNumber(Number(apiData.jumlah_unit_rejected)).withoutDecimal : "0"}`,
			  sideLabel: "TOTAL UNIT REJECTED",
			  value: apiData.total_unit_rejected ? convertNumber(Number(apiData.total_unit_rejected)).intoCurrency : "0",
			},
		  ])
		}
	},[totalSupplierPurchase.data])

	//State Filter
	const [fill, setFill] = useState<DashboardFillType>({
		month: moment().format("MM"),
		year: moment().format("YYYY"),
	})

	//ambil data chart menggunakan useChartList
	const { dataChart } = useChartList({
		date: `${fill.year}${fill.month}`,
		location_id: fill.location_id?.toString() || ""
	})

	return (
		<section className="container my-5">
			<section className="flex flex-col gap-3">
				<HeaderMenu title="PURCHASE DASHBOARD" />
		
				<DashboardFilter onChangeFilter={(res) => 
					{
						setFill({
							...fill,
							month: moment(res.date_from).format("MM"),
							year: moment(res.date_from).format("YYYY"),
							location_id: res.location_id
						})
						totalPurchaseSpend.refetch(
							{ 
								year_month: `${moment(res.date_from).format("YYYY")}${moment(res.date_from).format("MM")}`, 
								location_id: res.location_id
							}
						);

						totalSupplierPurchase.refetch(
							{ 
								year_month: `${moment(res.date_from).format("YYYY")}${moment(res.date_from).format("MM")}`, 
								location_id: res.location_id
							}
						);
					}
				} />

				<section className="flex flex-col gap-3">
					<section className="grid 2xl:grid-cols-7 xl:grid-cols-5 lg:grid-cols-4 gap md:grid-cols-3 sm:grid-cols-2 gap-3">
						{topCardList?.map((item, key) => <SummaryCard key={key} {...item} />)}
					</section>

					<section className="grid lg:grid-cols-3 sm:grid-cols-2 gap-3">
						
						{dataChart?.map(({ key, label, payload }) => {
							if (!payload || payload.length === 0) return null;

							const chartData = payload;
							const labels = chartData.map((item) => item.location_name);
							const totalList = chartData.map((item) => parseFloat(item.total));

							return (
								<SummaryBar key={key} label={label} labels={labels} totalList={totalList} />
							);
						})}
					</section>

					<section className="grid 2xl:grid-cols-7 xl:grid-cols-5 lg:grid-cols-4 gap md:grid-cols-3 sm:grid-cols-2 gap-3">
						{bottomCardList?.map((item, key) => <SummaryCard key={key} {...item} />)}
					</section>
				</section>
			</section>
		</section>
	)
}